// DEV-06 設定表單依層分區（SPEC #platform-ui-tiers、DEV-06 / DEV-07 / DEV-08、TIER-05）。純邏輯，畫面在 SettingsForm / ChannelMatrix。
//   - 層由 app-api 求值好放在 item.tier（hidden 的條目不會出現）；前端只信它，沒帶 = advanced（SPEC 的預設層）
//   - 設定分頁：common 在上、advanced 收進「進階（n）」；technical 全部在「技術」分頁（owner / admin）
//   - 「智慧分析」分頁：kinio_analytics 這個 cluster 的條目從設定分頁拿出來
//   - segment：同一個分段控制的成員併成一列，放在第一個成員的位置
import type { DeviceSettings, SettingItem, SettingSegment } from '../../lib/deviceApi';
import { enumText, itemLabel, kinioLabel, presetText } from '../../lib/kinioLabel';
import { diskKind, fmtBytes, parseSdCapacity, smartState, type DiskInfo } from '../../lib/deviceApi';

const SD_CAPACITY_KEY = 'kinio_storage.sd_capacity';
import i18n from 'i18next';
import { stamp } from '../../lib/timefmt';
import { modelLabel } from '../../lib/devices';

export type Tier = 'common' | 'advanced' | 'technical';
export const TIERS: Tier[] = ['common', 'advanced', 'technical'];
/** 總表 / 套設定 / 複製的預設篩選：只有常用 + 進階。 */
export const DEFAULT_TIERS: readonly Tier[] = ['common', 'advanced'];
export const ANALYTICS_CLUSTER = 'kinio_analytics';

export type SettingsView = 'settings' | 'analytics' | 'technical' | 'recording';

/**
 * 錄影與儲存的 section 只在「錄影與儲存」分頁畫（view = recording），設定分頁不再有「錄影」「儲存」子區
 * （SPEC #vms-web-327 進階分頁順序與長度：設定分區不重複錄影與儲存分頁；第二輪 R2-P1-5）。
 */
export const RECORDING_SECTIONS = new Set(['recording', 'storage']);

export const tierOf = (it: Pick<SettingItem, 'tier'>): Tier => it.tier ?? 'advanced';

/** 能看技術分頁 / 技術篩選晶片的角色（TIER-05）。 */
export const canSeeTechnical = (role: string): boolean => role === 'owner' || role === 'admin';

export type Row = { kind: 'item'; item: SettingItem } | { kind: 'segment'; name: string; members: SettingItem[] };

export interface RowGroup {
  cluster: string;
  label: string;
  rows: Row[];
}

export interface TierSection {
  name: string;
  label: string;
  /** 常用：section 內照投影順序平鋪 */
  common: Row[];
  /** 進階：照 cluster 分小標 */
  advanced: RowGroup[];
  /** 技術：照 cluster 分小標（只在 technical 檢視有） */
  technical: RowGroup[];
}

export const rowKey = (r: Row): string => (r.kind === 'item' ? r.item.key : `segment:${r.name}`);

/** 拿掉頁面別處已經管的設定列（key 清單）；拿完沒有列的區塊整個不畫。 */
export function hideRows(sections: TierSection[], hide?: string[]): TierSection[] {
  if (!hide?.length) return sections;
  const h = new Set(hide);
  const keep = (r: Row) => !(r.kind === 'item' && h.has(r.item.key));
  const groups = (gs: RowGroup[]) => gs.map((g) => ({ ...g, rows: g.rows.filter(keep) })).filter((g) => g.rows.length > 0);
  return sections
    .map((s) => ({ ...s, common: s.common.filter(keep), advanced: groups(s.advanced), technical: groups(s.technical) }))
    .filter((s) => s.common.length + rowCount(s.advanced) + rowCount(s.technical) > 0);
}
export const rowCount = (groups: RowGroup[]): number => groups.reduce((n, g) => n + g.rows.length, 0);

/** 把條目加進列清單；同一個 segment 的成員併進第一個成員那一列。 */
function pushRow(rows: Row[], it: SettingItem) {
  if (it.segment) {
    const seg = rows.find((r): r is Extract<Row, { kind: 'segment' }> => r.kind === 'segment' && r.name === it.segment);
    if (seg) seg.members.push(it);
    else rows.push({ kind: 'segment', name: it.segment, members: [it] });
    return;
  }
  rows.push({ kind: 'item', item: it });
}

/**
 * 只在專屬分頁畫、通用設定列表略過的 page（其他 page 照樣列在所屬 section）：
 * poe = kinio_station.poe_ports / poe_budget / poe_set，由「現場設備」分頁畫（SPEC #kinio-station-control「ui.yaml」、STN-05）。
 */
const OWN_PAGE_ONLY = new Set(['poe']);

function inView(cluster: string, view: SettingsView, section: string): boolean {
  if (view === 'analytics') return cluster === ANALYTICS_CLUSTER;
  if (view === 'settings') return cluster !== ANALYTICS_CLUSTER && !RECORDING_SECTIONS.has(section);
  if (view === 'recording') return RECORDING_SECTIONS.has(section);
  return true;
}

/**
 * 網頁做不到的列不畫（SPEC #vms-web-327「網頁做不到的不出現」、UX-11；VMS UX 評審 2026-10-13 B-28）：命令（button，例原廠重置、重新啟動）
 * 與沒有網頁編輯器的自訂項目只能在設備 App 操作，不顯示「請用設備 App 操作」；重新開機在設備詳細的「進階 › 管理」維護區。
 */
export function webCannotOperate(it: Pick<SettingItem, 'control' | 'editor'>): boolean {
  return !it.editor && (it.control === 'button' || it.control === 'custom');
}

/**
 * 設定表單 → 依層分區。
 *   settings / analytics：每個 section 的 common 與 advanced（technical 不列）；
 *   technical：每個 section 的 technical（含智慧分析的技術項）。
 * 沒有任何列的 section 不出現。
 */
export function layoutTiers(data: DeviceSettings | undefined, view: SettingsView): TierSection[] {
  const out: TierSection[] = [];
  for (const s of data?.sections ?? []) {
    const sec: TierSection = { name: s.name, label: kinioLabel(s.label_id, s.label), common: [], advanced: [], technical: [] };
    for (const c of s.clusters) {
      if (!inView(c.cluster, view, s.name)) continue;
      const group = (): RowGroup => ({ cluster: c.cluster, label: kinioLabel(c.label_id, c.label), rows: [] });
      const adv = group();
      const tech = group();
      for (const it of c.items) {
        if (OWN_PAGE_ONLY.has(it.page ?? '')) continue;
        if (webCannotOperate(it)) continue;
        const tier = tierOf(it);
        if (view === 'technical') {
          if (tier === 'technical') pushRow(tech.rows, it);
        } else if (tier === 'common') pushRow(sec.common, it);
        else if (tier === 'advanced') pushRow(adv.rows, it);
      }
      if (adv.rows.length) sec.advanced.push(adv);
      if (tech.rows.length) sec.technical.push(tech);
    }
    if (sec.common.length || sec.advanced.length || sec.technical.length) out.push(sec);
  }
  return out;
}

/** 「智慧分析」分頁出不出現：有 kinio_analytics 而且有非技術的條目。 */
export function hasAnalytics(data: DeviceSettings | undefined): boolean {
  return layoutTiers(data, 'analytics').length > 0;
}

/** 技術分頁有沒有東西。 */
export function hasTechnical(data: DeviceSettings | undefined): boolean {
  return layoutTiers(data, 'technical').length > 0;
}

/**
 * 韌體版本只顯示一個（B-11）：平台登記的 OTA 版號在概覽 / 韌體分頁。設備自報的 basic_information 字串跟它不一樣時
 * （NVR 的原廠字串，例 V1.0.0-20260913）改名「原廠韌體版本」、放到技術分頁；一樣就照舊。
 */
export const VENDOR_FW_KEY = 'basic_information.software_version_string';
export function withVendorFirmware(data: DeviceSettings, ota: string | undefined): DeviceSettings {
  const fix = (it: SettingItem): SettingItem =>
    it.key === VENDOR_FW_KEY && typeof it.value === 'string' && it.value && it.value !== ota
      ? { ...it, label: i18n.t('dset.vendor_fw'), label_id: undefined, tier: 'technical' }
      : it;
  return { ...data, sections: data.sections.map((s) => ({ ...s, clusters: s.clusters.map((c) => ({ ...c, items: c.items.map(fix) })) })) };
}

export const allItems = (data: DeviceSettings | undefined): SettingItem[] =>
  (data?.sections ?? []).flatMap((s) => s.clusters.flatMap((c) => c.items));

const isBytes = (key: string, units?: string) => /^bytes?$/i.test(units ?? '') || /_bytes$/.test(key);

/** 一顆碟一行：型號 · 種類 · 容量（可用）· 狀態（B-12：不畫 JSON） */
function diskText(d: DiskInfo): string {
  const kind = diskKind(d);
  const total = d.total_bytes ?? d.totalBytes;
  const free = d.free_bytes ?? d.freeBytes;
  const smart = d.smart !== undefined ? smartState(d.smart) : undefined;
  return [
    d.model || d.device || (d.no !== undefined ? `#${d.no}` : ''),
    kind ? i18n.t(`health.kind_${kind}`) : '',
    total !== undefined ? fmtBytes(total) + (free !== undefined ? i18n.t('dset.val_free', { v: fmtBytes(free) }) : '') : '',
    smart ? i18n.t(`health.smart_${smart}`) : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** 物件 / 陣列值：碟清單逐顆一行；其他物件列出「欄位：值」（bytes 換成 GB），不出現 JSON 的括號。 */
function objectText(it: SettingItem, v: object): string {
  if (Array.isArray(v)) {
    if (!v.length) return i18n.t('dset.val_none');
    if (it.key.endsWith('.disks') || v.every((x) => x && typeof x === 'object' && ('total_bytes' in x || 'model' in x))) return v.map((d) => diskText(d as DiskInfo)).join('\n');
    return v.map((x) => (x && typeof x === 'object' ? objectText(it, x) : scalarText(it.key, undefined, x))).join('\n');
  }
  return Object.entries(v)
    .filter(([, x]) => x !== null && x !== undefined && typeof x !== 'object')
    .map(([k, x]) => `${k}: ${scalarText(k, undefined, x)}`)
    .join(' · ');
}

/** 單一值（bytes → GB、kbps ≥ 1000 → Mbps、unknown → 未知、9999-12-31 → 不會過期、epoch 秒 0 → 未設定；SPEC #vms-web-327 DEV-06）。 */
/**
 * 畫面上要印的單位（第十二輪 B12-09：設備目錄的 units 有時是值域「0..100」，Node 設定印成「訊號強度 100 0..100」）：
 * 值域字串（含 `..` 或 `|`）不印——欄位名帶 percent（*_percent、*_percent_*）的寫「%」；`percent` / `%` 也寫「%」（protocol 0afa886 起 units 就是 %，不會變兩個 %）；其他照原字。
 */
export function displayUnits(key: string, units: string | undefined): string | undefined {
  const u = (units ?? '').trim();
  if (!u) return undefined;
  // 欄位名帶 percent（wifi_signal_percent、bat_percent_remaining）的值域寫「%」（第十三輪：電量原本只對 *_percent 結尾，87 不帶單位）
  if (/\.\.|[|]/.test(u)) return /(^|[._])percent([._]|$)/.test(key) ? '%' : undefined;
  if (/^(percent|%)$/i.test(u)) return '%';
  return u;
}

function scalarText(key: string, unitsIn: string | undefined, v: unknown): string {
  // 能力模型沒寫單位、但欄位名就是單位的（kinio_storage.record_kbps「寫入速率 836」，第三輪 B3-11）：照欄位名補
  const units = displayUnits(key, unitsIn) ?? (/_kbps$/.test(key) ? 'kbps' : undefined);
  if (typeof v === 'number' && isBytes(key, units)) return fmtBytes(v);
  if (typeof v === 'number' && /^kbps$/i.test((units ?? '').trim()) && v >= 1000) return `${(v / 1000).toFixed(2).replace(/\.?0+$/, '')} Mbps`;
  if (typeof v === 'number' && /epoch/i.test(units ?? '')) return v === 0 ? i18n.t('dset.val_locked') : stamp(v * 1000);
  if (typeof v === 'string') {
    if (/^(unknown|n\/?a)$/i.test(v.trim())) return i18n.t('dset.val_unknown');
    if (/^9999-12-31/.test(v)) return i18n.t('dset.val_never');
  }
  if (units === '%') return `${String(v)}%`;
  return String(v) + (units && !/epoch/i.test(units) ? ` ${units}` : '');
}

/** Kinio Node 回報的 UART1 接線（shadow kinio_node.ttl_dut，SPEC #node-s11-7-3）。 */
export const TTL_DUT_KEY = 'kinio_node.ttl_dut';
export function ttlDutText(v: unknown): string | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const s = (x: unknown) => (typeof x === 'string' && x ? x : '');
  const label = s(o.label);
  if (!label) return null;
  const model = s(o.model);
  const aid = s(o.aid);
  return [model ? i18n.t('dset.ttl_dut_model', { label, model: modelLabel(model) }) : label, aid ? i18n.t('dset.ttl_dut_aid', { aid }) : ''].filter(Boolean).join(' · ');
}

/** NVR 的通道摘要（kinio_system.channel_online_summary）：「1 路在線 / 共 3 路」，不寫「1/3」（第二輪 R2-P1-5）。 */
export const CH_SUMMARY_KEY = 'kinio_system.channel_online_summary';
export function channelSummaryText(v: unknown): string | null {
  let online: number | undefined;
  let total: number | undefined;
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    online = typeof o.online_count === 'number' ? o.online_count : undefined;
    total = typeof o.total === 'number' ? o.total : typeof o.offline_count === 'number' && online !== undefined ? online + o.offline_count : undefined;
  } else if (typeof v === 'string') {
    const m = /^\s*(\d+)\s*\/\s*(\d+)\s*$/.exec(v);
    if (m) [online, total] = [Number(m[1]), Number(m[2])];
  }
  if (online === undefined || total === undefined) return null;
  return i18n.t('dset.ch_summary', { online, total });
}

/** 值的顯示文字：檔位 → enum 文案 → 開 / 關（有 / 沒有）→ 數字加單位（bytes 換成 GB）；物件不畫 JSON。 */
export function settingText(it: SettingItem, v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (it.key === CH_SUMMARY_KEY) {
    const s = channelSummaryText(v);
    if (s) return s;
  }
  // 記憶卡容量（"total/used/free" MB）：寫「已用 X / 共 Y（可用 Z）」，跟儲存摘要同一份；讀不到寫「讀不到容量」（第四輪 B4-03）
  if (it.key === SD_CAPACITY_KEY) {
    const c = parseSdCapacity(v);
    return c ? i18n.t('health.cap_used_total_free', { used: fmtBytes(c.used), total: fmtBytes(c.total), free: fmtBytes(c.free) }) : i18n.t('health.space_unknown');
  }
  if (typeof v === 'number') {
    const p = it.presets?.find((x) => x.value === v);
    if (p) return presetText(p);
  }
  if (it.enum && (typeof v === 'number' || typeof v === 'string')) return enumText(it, v);
  // 布林值寫中文（第三輪 R3-P1-8：「記憶卡 ON」）：_present 這類「有沒有」寫有 / 沒有，其他寫開 / 關
  if (typeof v === 'boolean') return /_present$/.test(it.key) ? i18n.t(v ? 'dset.val_present' : 'dset.val_absent') : i18n.t(v ? 'dset.val_on' : 'dset.val_off');
  // Kinio Node 的 UART1 接線（kinio_node.ttl_dut {label, aid, model}；NODE-06）：「PAC122 工程機（PAC122）· AID CFL-…」，不寫欄位名
  if (it.key === TTL_DUT_KEY) {
    const s = ttlDutText(v);
    if (s) return s;
  }
  if (typeof v === 'object') return objectText(it, v);
  // 硬碟健康這類狀態字串：照健康分頁同一組譯名
  if (typeof v === 'string' && /\.smart$/.test(it.key)) return i18n.t(`health.smart_${smartState(v)}`);
  // 型號一律跟清單、韌體頁同一種寫法（大寫 / Kinio Station；第五輪 B5-18：NVR 設定 › 設備不再寫「se-nt98631-k1」）
  if (typeof v === 'string' && /(^|\.)(model|product_name)$/.test(it.key)) return modelLabel(v);
  return scalarText(it.key, it.units, v);
}

/**
 * 能力投影的說明（it.help）只給使用者看得懂的（B-13 / P0-7 ④）：設備目錄裡有些是開發備註
 * （SPEC / ADR 編號、AlphaSeries 對照、「值域待實機量」「App 不顯示」「只有平台寫」…），這些不上畫面。
 * 正本要在 protocol model/ui.yaml 改（help 只寫使用者語言、開發備註另放），這裡是畫面端的防線。
 */
const DEV_HELP = /SPEC|ADR-|§|AlphaSeries|Swann|slot|wire|enum|presets?\b|range|admin|App|平台|開發|待實機|待定|未定|語意|不給使用者|不是使用者|不是設定|不當設定|重複|精靈|編輯器|由「|keepalive|EDCCA|basic_information|kinio_|頁(顯示|的|會)|已顯示|呼叫|觸發|指派|送出|卡顯示|承接值/i;
/** 譯文（en / ja / zh-Hans）裡的開發備註字樣。 */
const DEV_HELP_TR = /still to be|to be measured|afterwards switch|\bTBD\b|config\.[a-z]|実機で計測|计測|实机|待实|プリセットに変更/i;
/** 這段說明（原文或譯文）是開發備註、畫面不顯示（ui_banned 檢查用來確認設備目錄裡的開發備註不會上畫面）。 */
export function isDevHelp(text: string): boolean {
  return DEV_HELP.test(text) || DEV_HELP_TR.test(text);
}
export function userHelp(help: string | undefined, key?: string): string | undefined {
  if (!help) return undefined;
  // 判斷用投影的 zh-Hant 原文（開發備註的字樣在原文裡）；要顯示的照語系翻（`<key>.help`，B-22）
  if (DEV_HELP.test(help)) return undefined;
  if (!key) return help;
  // 譯文也要過一次（第二輪 B2-07：中文清了、英文「still to be measured … config.soundDLvl」還在）；譯文是開發備註就不顯示
  const tr = kinioLabel(`${key}.help`, help);
  return DEV_HELP.test(tr) || DEV_HELP_TR.test(tr) ? undefined : tr;
}

// ---- depends_on / depends_value ----

/**
 * 依賴的上層開著才能改：上層是 enum 且有 depends_value → 值在清單內；否則上層要是 true。
 * 上層這台沒回報（不在表單裡）→ 不擋。
 */
export function dependsOk(it: SettingItem, byKey: Map<string, SettingItem>): boolean {
  if (!it.depends_on) return true;
  const p = byKey.get(it.depends_on);
  if (!p) return true;
  if (it.depends_value?.length) return typeof p.value === 'number' && it.depends_value.includes(p.value);
  return p.value === true;
}

// ---- segment（分段控制） ----

export const SEGMENT_NONE = 'none';

/** 目前選的段：第一個為 true 的成員，否則 none。 */
export function segmentCurrent(members: SettingItem[]): string {
  return members.find((m) => m.value === true)?.key ?? SEGMENT_NONE;
}

/** 選某一段要寫的 desired：選成員 = 那個 true、其他 false（同一次）；選 none = 全部 false。 */
export function segmentDesired(members: SettingItem[], choice: string): Record<string, Record<string, boolean>> {
  const st: Record<string, Record<string, boolean>> = {};
  for (const m of members) {
    const [cluster, attr] = m.key.split('.');
    (st[cluster] ??= {})[attr] = m.key === choice;
  }
  return st;
}

/** 段的選項：[none_label, 成員 label…]（成員照投影順序）。 */
export function segmentOptions(name: string, members: SettingItem[], decl: SettingSegment | undefined): { id: string; label: string }[] {
  return [
    { id: SEGMENT_NONE, label: decl ? kinioLabel(decl.none_label_id, decl.none_label) : name },
    ...members.map((m) => ({ id: m.key, label: itemLabel(m) })),
  ];
}

// ---- presets ----

/** presets 選單目前選的值：在檔位裡 = 該值字串；不在（例 240 秒）= ''，選單顯示格式化後的目前值但不加選項。 */
export function presetSelected(it: SettingItem, v: unknown): string {
  return typeof v === 'number' && (it.presets ?? []).some((p) => p.value === v) ? String(v) : '';
}

// ---- 進階折疊記憶（每人每 section，存在這個瀏覽器） ----

export const advStorageKey = (user: string, section: string): string => `vms.settings.adv.${user}.${section}`;

export function readAdvOpen(user: string, section: string): boolean {
  try {
    return localStorage.getItem(advStorageKey(user, section)) === '1';
  } catch {
    return false;
  }
}

export function writeAdvOpen(user: string, section: string, open: boolean): void {
  try {
    if (open) localStorage.setItem(advStorageKey(user, section), '1');
    else localStorage.removeItem(advStorageKey(user, section));
  } catch {
    /* 無痕 / 封鎖 storage：只是不記 */
  }
}

// ---- 篩選晶片（DEV-07 總表、DEV-08 套設定、複製） ----

export const inTiers = (it: Pick<SettingItem, 'tier'>, tiers: ReadonlySet<Tier>): boolean => tiers.has(tierOf(it));
